import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, ChevronDown, X } from "lucide-react";
import { toast } from "react-toastify";
import RightDrawer from "../../RightDrawer";
import { api } from "../../../services";
import "./BusinessInfoModal.css";

const FIELDS = [
  { key: "crNumber", label: "crNumber" },
  { key: "legalTradeName", label: "legalTradeName" },
  { key: "registeredAddress", label: "registeredAddress" },
];

const REASON_IDS = [
  "addressMismatch",
  "nameMismatch",
  "crMismatch",
  "unreadable",
  "insufficient",
];

function formatSize(bytes) {
  const size = Number(bytes) || 0;
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  const mb = size / (1024 * 1024);
  return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
}

function norm(value) {
  return String(value || "").trim();
}

function fileKind(file) {
  const name = String(file?.originalName || "").toLowerCase();
  const mime = String(file?.mime || "").toLowerCase();
  if (name.endsWith(".pdf") || mime === "application/pdf") return "PDF";
  if (mime.startsWith("image/") || /\.(png|jpe?g|gif|webp)$/.test(name)) return "Image";
  const ext = name.split(".").pop();
  return ext ? ext.toUpperCase() : "File";
}

function isImage(file) {
  const mime = String(file?.mime || "").toLowerCase();
  const name = String(file?.originalName || "").toLowerCase();
  return mime.startsWith("image/") || /\.(png|jpe?g|gif|webp)$/.test(name);
}

function isPdf(file) {
  const mime = String(file?.mime || "").toLowerCase();
  const name = String(file?.originalName || "").toLowerCase();
  return mime === "application/pdf" || name.endsWith(".pdf");
}

function BusinessInfoModal({ show, user, onClose, onSaved }) {
  const { t, i18n } = useTranslation("translation");
  const tb = (key, opts) => t(`adminBusinessInfo.${key}`, opts);

  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [tab, setTab] = useState("info");
  const [approvedOpen, setApprovedOpen] = useState(true);
  const [reviewed, setReviewed] = useState(() => new Set());
  const [rejecting, setRejecting] = useState(false);
  const [reasonId, setReasonId] = useState("");
  const [message, setMessage] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [openPreviewId, setOpenPreviewId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewUrlRef = useRef("");

  const clearPreview = () => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
    }
    setPreviewUrl("");
    setOpenPreviewId(null);
  };

  useEffect(() => {
    if (!show || !user?._id) {
      clearPreview();
      return undefined;
    }

    let cancelled = false;
    setFetching(true);
    setView(null);
    setLoadFailed(false);
    setTab("info");
    setApprovedOpen(true);
    setReviewed(new Set());
    setRejecting(false);
    setReasonId("");
    setMessage("");
    setReasonError("");
    clearPreview();

    const token = localStorage.getItem("adminToken");
    api
      .get(`/admin/users/${user._id}/business`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) setView(res.data || {});
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, user?._id]);

  useEffect(() => () => {
    if (previewUrlRef.current) window.URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const info = view?.businessInfo || {};
  const approved = view?.approvedInUse || null;
  const isUpdate = Boolean(approved);
  const documents = view?.documents || [];
  const canDecide = Boolean(view?.canDecide) && view?.profileStatus !== "approved";

  const rows = useMemo(
    () =>
      FIELDS.map((field) => {
        const value = norm(info[field.key]);
        const previous = norm(approved?.[field.key]);
        const changed = isUpdate && value !== previous;
        return { ...field, value, previous, changed };
      }),
    [info, approved, isUpdate]
  );

  const changedCount = rows.filter((row) => row.changed).length;
  const reviewedCount = documents.filter((file) => reviewed.has(file.id)).length;
  const allReviewed = documents.length === 0 || reviewedCount === documents.length;
  const alreadyRejected = view?.profileStatus === "rejected";
  const approveDisabled = loading || fetching || !canDecide || !allReviewed;
  const reviewHint = documents.length
    ? tb("reviewBeforeRejectingProgress", { reviewed: reviewedCount, total: documents.length })
    : tb("reviewBeforeRejecting");

  const toggleReviewed = (id) => {
    setReviewed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setReviewed((prev) => {
      if (documents.every((file) => prev.has(file.id))) return new Set();
      return new Set(documents.map((file) => file.id));
    });
  };

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  });

  const togglePreview = async (file) => {
    if (openPreviewId === file.id) {
      clearPreview();
      return;
    }
    clearPreview();
    setOpenPreviewId(file.id);
    if (!isImage(file) && !isPdf(file)) return;
    setPreviewLoading(true);
    try {
      const response = await api.get(
        `/admin/users/${user._id}/business/documents/${file.id}`,
        { responseType: "blob", headers: authHeaders() }
      );
      const type = file.mime || response.headers?.["content-type"] || "";
      const blob = new Blob([response.data], type ? { type } : undefined);
      const url = window.URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch (err) {
      toast.error(err.response?.data?.message || tb("decisionError"));
      setOpenPreviewId(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const submitDecision = async (status) => {
    if (!canDecide || loading) return;
    if (status === "rejected" && alreadyRejected) return;
    let rejectionReason;
    if (status === "rejected") {
      if (!reasonId) {
        setReasonError(tb("selectReason"));
        return;
      }
      const reason = tb(`reasons.${reasonId}`);
      const note = message.trim();
      rejectionReason = note ? `${reason}\n${note}` : reason;
      if (rejectionReason.length > 250) {
        setReasonError(tb("rejectionInvalid"));
        return;
      }
    }
    setReasonError("");
    setLoading(true);
    try {
      const response = await api.put(
        `/admin/users/${user._id}/business/decision`,
        { status, rejectionReason },
        { headers: { ...authHeaders(), "Content-Type": "application/json" } }
      );
      toast.success(response.data?.message || tb("decisionSuccess"));
      setView(response.data);
      setRejecting(false);
      onSaved?.(response.data);
    } catch (err) {
      toast.error(err.response?.data?.message || tb("decisionError"));
    } finally {
      setLoading(false);
    }
  };

  const describeFile = (file) => {
    const parts = [fileKind(file)];
    const size = formatSize(file.size);
    if (size) parts.push(size);
    if (isUpdate) {
      const replaced = (approved?.documents || []).some(
        (doc) => doc.originalName === file.originalName
      );
      parts.push(replaced ? tb("replacedDoc") : tb("newDoc"));
    }
    return parts.join(" · ");
  };

  return (
    <RightDrawer
      open={show}
      onClose={onClose}
      hideHeader
      width="560px"
      dir={i18n.dir()}
      panelClassName="admin-biz-drawer"
    >
      <div className="admin-biz-drawer__shell">
        <header className="admin-biz-drawer__header">
          <div className="admin-biz-drawer__heading">
            <Building2 size={22} strokeWidth={1.6} aria-hidden="true" />
            <h2>{tb("title")}</h2>
            {isUpdate ? <span className="admin-biz-drawer__pill">{tb("profileUpdate")}</span> : null}
          </div>
          <button type="button" className="admin-biz-drawer__close" onClick={onClose} aria-label={t("close", { defaultValue: "Close" })}>
            <X size={24} strokeWidth={1.7} aria-hidden="true" />
          </button>
        </header>

        {!fetching && !loadFailed && view?.canDecide ? (
          <div className="admin-biz-drawer__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "info"}
              className={`admin-biz-drawer__tab${tab === "info" ? " is-active" : ""}`}
              onClick={() => setTab("info")}
            >
              {tb("title")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "documents"}
              className={`admin-biz-drawer__tab${tab === "documents" ? " is-active" : ""}`}
              onClick={() => setTab("documents")}
            >
              {tb("tabDocuments")}
              <span className="admin-biz-drawer__count">
                {tb("docProgress", { reviewed: reviewedCount, total: documents.length })}
              </span>
            </button>
          </div>
        ) : null}

        <div className="admin-biz-drawer__body">
          {fetching ? (
            <p className="admin-biz-drawer__empty">{t("Processing")}...</p>
          ) : loadFailed ? (
            <p className="admin-biz-drawer__empty">{tb("decisionError")}</p>
          ) : !view?.canDecide ? (
            <p className="admin-biz-drawer__empty">{tb("notSubmitted")}</p>
          ) : tab === "documents" ? (
            <section className="admin-biz-docs">
              <h3>{tb("uploadedDocuments")}</h3>
              <label className="admin-biz-docs__all">
                <span>
                  <input
                    type="checkbox"
                    checked={documents.length > 0 && allReviewed}
                    onChange={toggleAll}
                    disabled={!documents.length}
                  />
                  {tb("markAllReviewed")}
                </span>
                <strong>{tb("reviewedOf", { reviewed: reviewedCount, total: documents.length })}</strong>
              </label>
              {documents.length ? (
                documents.map((file) => {
                  const open = openPreviewId === file.id;
                  return (
                    <article key={file.id} className="admin-biz-doc">
                      <div className="admin-biz-doc__row">
                        <input
                          type="checkbox"
                          checked={reviewed.has(file.id)}
                          onChange={() => toggleReviewed(file.id)}
                          aria-label={file.originalName}
                        />
                        <div className="admin-biz-doc__text">
                          <strong>{file.originalName}</strong>
                          <span>{describeFile(file)}</span>
                        </div>
                        <button
                          type="button"
                          className={`admin-biz-doc__toggle${open ? " is-open" : ""}`}
                          aria-expanded={open}
                          aria-label={tb("preview")}
                          onClick={() => togglePreview(file)}
                        >
                          <ChevronDown size={20} strokeWidth={1.5} aria-hidden="true" />
                        </button>
                      </div>
                      {open ? (
                        <div className="admin-biz-doc__preview">
                          {previewLoading ? (
                            <p>{t("Processing")}...</p>
                          ) : isImage(file) && previewUrl ? (
                            <img src={previewUrl} alt={file.originalName} />
                          ) : isPdf(file) && previewUrl ? (
                            <iframe title={file.originalName} src={previewUrl} />
                          ) : (
                            <p>{tb("previewUnavailable")}</p>
                          )}
                        </div>
                      ) : null}
                    </article>
                  );
                })
              ) : (
                <p className="admin-biz-drawer__empty">{tb("noDocuments")}</p>
              )}
            </section>
          ) : (
            <section className="admin-biz-fields">
              <h3>
                {isUpdate ? tb("requestedChanges") : tb("submittedInformation")}
                {isUpdate ? <span className="admin-biz-drawer__count">{changedCount}</span> : null}
              </h3>
              {rows.map((row) => (
                <div key={row.key} className="admin-biz-field">
                  <span className="admin-biz-field__label">{tb(row.label)}</span>
                  <div className={`admin-biz-field__value${row.changed ? " is-changed" : ""}`}>
                    {row.value || "—"}
                  </div>
                  {row.changed ? (
                    <p className="admin-biz-field__was">
                      <span>{tb("was")}</span>
                      <s>{row.previous || "—"}</s>
                    </p>
                  ) : null}
                </div>
              ))}
              {isUpdate ? (
                <div className="admin-biz-approved">
                  <button type="button" className="admin-biz-approved__toggle" onClick={() => setApprovedOpen((open) => !open)} aria-expanded={approvedOpen}>
                    <span>{tb("currentlyApprovedProfile")}</span>
                    <ChevronDown size={20} className={approvedOpen ? "is-open" : ""} aria-hidden="true" />
                  </button>
                  {approvedOpen ? (
                    <dl>
                      {FIELDS.map((field) => (
                        <div key={field.key}>
                          <dt>{tb(field.label)}</dt>
                          <dd>{norm(approved[field.key]) || "—"}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                </div>
              ) : null}
            </section>
          )}
        </div>

        {canDecide && !fetching && !loadFailed ? (
          rejecting ? (
            <footer className="admin-biz-footer admin-biz-footer--reject">
              <p className="admin-biz-footer__hint">{reviewHint}</p>
              <label>
                <span>{tb("rejectionReason")}</span>
                <select
                  value={reasonId}
                  onChange={(event) => {
                    setReasonId(event.target.value);
                    setReasonError("");
                  }}
                >
                  <option value="">{tb("selectReason")}</option>
                  {REASON_IDS.map((id) => (
                    <option key={id} value={id}>
                      {tb(`reasons.${id}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>{tb("messageToBusiness")}</span>
                <textarea
                  rows={2}
                  maxLength={180}
                  value={message}
                  placeholder={tb("messagePlaceholder")}
                  onChange={(event) => setMessage(event.target.value)}
                />
              </label>
              {reasonError ? <p className="admin-biz-footer__error">{reasonError}</p> : null}
              <div className="admin-biz-footer__actions">
                <button type="button" className="admin-biz-btn admin-biz-btn--back" onClick={() => setRejecting(false)} disabled={loading}>
                  {tb("back")}
                </button>
                <button
                  type="button"
                  className="admin-biz-btn admin-biz-btn--confirm"
                  disabled={loading || !reasonId || alreadyRejected}
                  onClick={() => submitDecision("rejected")}
                >
                  {tb("confirmRejection")}
                </button>
              </div>
            </footer>
          ) : (
            <footer className="admin-biz-footer">
              <p className="admin-biz-footer__hint">{reviewHint}</p>
              <div className="admin-biz-footer__actions">
                <button
                  type="button"
                  className="admin-biz-btn admin-biz-btn--reject"
                  onClick={() => setRejecting(true)}
                  disabled={loading || alreadyRejected}
                >
                  {tb("reject")}
                </button>
                <button
                  type="button"
                  className="admin-biz-btn admin-biz-btn--approve"
                  disabled={approveDisabled}
                  onClick={() => submitDecision("approved")}
                >
                  {tb("approve")}
                </button>
              </div>
            </footer>
          )
        ) : null}
      </div>
    </RightDrawer>
  );
}

export default BusinessInfoModal;
